import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ClockCounterClockwise,
  CaretDoubleRight,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "Gerade eben";
    const min = Math.floor(sec / 60);
    if (min < 60) return `Vor ${min} ${min === 1 ? "Minute" : "Minuten"}`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Vor ${hr} ${hr === 1 ? "Stunde" : "Stunden"}`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `Vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
    const months = Math.floor(days / 30);
    return `Vor ${months} ${months === 1 ? "Monat" : "Monaten"}`;
  } catch {
    return "—";
  }
}

/**
 * ClassificationEventsTable
 *
 * Reusable searchable + paginated activity table that hits
 * GET /api/admin/classification-events?q=…&user_id=…&limit=…&offset=….
 *
 * Used in 2 contexts:
 *   * AdminStatisticsPage   → no userId, full search across all events
 *   * AdminUsersPage modal  → userId set, narrow scope to one user
 *
 * The user-column is hidden when scoped to a single user (saves whitespace).
 */
export default function ClassificationEventsTable({
  userId = "",
  pageSize = 25,
  hideUserColumn = false,
  emptyMessage = "Keine Aktivitäten gefunden.",
  className = "",
  testIdPrefix = "events",
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  // Debounce the query so we don't hammer the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset pagination when search or scope changes
  useEffect(() => {
    setOffset(0);
  }, [debounced, userId]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", String(pageSize));
        params.set("offset", String(offset));
        if (debounced) params.set("q", debounced);
        if (userId) params.set("user_id", userId);
        const { data } = await api.get(`/admin/classification-events?${params.toString()}`);
        if (!alive) return;
        setItems(data?.items || []);
        setTotal(data?.total || 0);
      } catch (err) {
        if (alive) toast.error(extractError(err, "Aktivitäten konnten nicht geladen werden."));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [debounced, userId, offset, pageSize]);

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + items.length, total);
  const canPrev = offset > 0;
  const canNext = offset + pageSize < total;

  const colCount = useMemo(() => (hideUserColumn ? 4 : 5), [hideUserColumn]);

  return (
    <div className={`border border-ink-200 bg-white rounded-sm ${className}`} data-testid={`${testIdPrefix}-card`}>
      <div className="px-6 py-5 border-b border-ink-200 flex items-center gap-3 flex-wrap">
        <ClockCounterClockwise size={16} weight="duotone" className="text-ink-700 shrink-0" />
        <h2 className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight flex-1 min-w-0">
          Letzte Aktivitäten
        </h2>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass
            size={14}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500 pointer-events-none"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="E-Mail, Name, KV, Gruppe …"
            data-testid={`${testIdPrefix}-search`}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-sm border border-ink-300 bg-white placeholder:text-warm-400 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
          <thead>
            <tr className="border-b border-ink-200 bg-warm-50">
              {[
                "Zeitpunkt",
                "Kollektivvertrag",
                "Gruppe",
                "Stufe",
                ...(hideUserColumn ? [] : ["Benutzer"]),
              ].map((h) => (
                <th
                  key={h}
                  className="text-left px-5 py-3 text-[10px] uppercase tracking-widest font-semibold text-warm-600"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-5 py-10 text-center text-sm text-warm-600">
                  Wird geladen …
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-5 py-10 text-center text-sm text-warm-600"
                  data-testid={`${testIdPrefix}-empty`}
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {items.map((e) => (
              <tr
                key={e.id}
                data-testid={`${testIdPrefix}-row-${e.id}`}
                className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50 transition-crisp"
              >
                <td className="px-5 py-3.5 align-top">
                  <div className="text-sm text-ink-900 font-medium">{fmtRelative(e.completed_at)}</div>
                  <div className="text-[11px] text-warm-500 mt-0.5">{fmtDate(e.completed_at)}</div>
                </td>
                <td className="px-5 py-3.5 align-top text-sm text-ink-900">{e.kv_name}</td>
                <td className="px-5 py-3.5 align-top">
                  {e.suggested_group ? (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold border border-brand-300 bg-brand-50 text-ink-900 rounded-sm">
                      {e.suggested_group}
                    </span>
                  ) : (
                    <span className="text-xs text-warm-400">—</span>
                  )}
                </td>
                <td className="px-5 py-3.5 align-top text-sm text-warm-700">
                  {e.suggested_stufe ? e.suggested_stufe : <span className="text-xs text-warm-400">—</span>}
                </td>
                {!hideUserColumn && (
                  <td className="px-5 py-3.5 align-top">
                    <div className="text-sm text-ink-900">{e.user_name || e.user_email}</div>
                    <div className="text-[11px] text-warm-500 truncate">
                      {e.user_company ? `${e.user_company} · ` : ""}
                      {e.user_email}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 border-t border-ink-200 bg-warm-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-warm-600">
          {total === 0 ? (
            "0 Treffer"
          ) : (
            <>
              <strong className="text-ink-900">{showingFrom}-{showingTo}</strong> von{" "}
              <strong className="text-ink-900">{total}</strong>
              {debounced && <> — gefiltert nach „{debounced}"</>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            data-testid={`${testIdPrefix}-prev`}
            className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border border-ink-300 bg-white text-ink-900 hover:bg-ink-100 transition-crisp disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CaretDoubleRight size={12} weight="bold" className="rotate-180 inline mr-1" />
            Zurück
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setOffset((o) => o + pageSize)}
            data-testid={`${testIdPrefix}-next`}
            className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border border-ink-300 bg-white text-ink-900 hover:bg-ink-100 transition-crisp disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Weiter
            <CaretDoubleRight size={12} weight="bold" className="inline ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}
